// Multi-Wallet Dashboard Types

export interface ConnectedWallet {
  id: string;
  address: string;
  label: string;
  type: WalletProviderType;
  chainId: number;
  isActive: boolean; // Currently selected wallet
  addedAt: number;
  lastSynced: number;
}

export type WalletProviderType =
  | 'metamask'
  | 'walletconnect'
  | 'thor_wallet'
  | 'coinbase'
  | 'ledger'
  | 'manual'; // Read-only/watch-only wallet

export interface WalletBalance {
  walletId: string;
  tokens: TokenBalance[];
  totalValueUSD: number;
  change24h: number;
  change24hPercent: number;
}

export interface TokenBalance {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  balance: string;
  balanceFormatted: string;
  price: number;
  value: number;
  change24h: number;
  change24hPercent: number;
  logoURI?: string;
  chainId: number;
}

export interface AggregatedPortfolio {
  totalValueUSD: number;
  change24h: number;
  change24hPercent: number;
  walletCount: number;
  chainBreakdown: ChainBreakdown[];
  tokenBreakdown: TokenBreakdown[];
  topHoldings: TokenBalance[];
}

export interface ChainBreakdown {
  chainId: number;
  chainName: string;
  chainIcon?: string;
  totalValue: number;
  percentage: number;
  walletCount: number;
}

export interface TokenBreakdown {
  symbol: string;
  name: string;
  totalBalance: string;
  totalValue: number;
  percentage: number;
  walletAddresses: string[]; // Which wallets hold this token
  averagePrice: number;
}

export interface MultiWalletStats {
  totalWallets: number;
  activeWallets: number;
  totalValue: string;
  bestPerformer: {
    walletLabel: string;
    change24hPercent: number;
  };
  worstPerformer: {
    walletLabel: string;
    change24hPercent: number;
  };
  mostDiversified: {
    walletLabel: string;
    tokenCount: number;
  };
}

export interface WalletTransaction {
  id: string;
  walletId: string;
  walletAddress: string;
  walletLabel: string;
  hash: string;
  timestamp: number;
  type: 'send' | 'receive' | 'swap' | 'stake' | 'unstake' | 'approve' | 'contract';
  status: 'pending' | 'confirmed' | 'failed';
  token: {
    symbol: string;
    amount: string;
    valueUSD: number;
  };
  to?: string;
  from?: string;
  gasFee: string;
  chainId: number;
}

// Chain configuration
export const SUPPORTED_CHAINS: Record<number, { name: string; icon: string; color: string }> = {
  1: { name: 'Ethereum', icon: 'ETH', color: '#627EEA' },
  56: { name: 'BNB Chain', icon: 'BNB', color: '#F3BA2F' },
  137: { name: 'Polygon', icon: 'MATIC', color: '#8247E5' },
  42161: { name: 'Arbitrum', icon: 'ARB', color: '#28A0F0' },
  10: { name: 'Optimism', icon: 'OP', color: '#FF0420' },
  43114: { name: 'Avalanche', icon: 'AVAX', color: '#E84142' },
  250: { name: 'Fantom', icon: 'FTM', color: '#1969FF' },
  8453: { name: 'Base', icon: 'BASE', color: '#0052FF' },
};

export const WALLET_PROVIDER_INFO: Record<WalletProviderType, { name: string; icon: string }> = {
  metamask: { name: 'MetaMask', icon: '🦊' },
  walletconnect: { name: 'WalletConnect', icon: '🔗' },
  thor_wallet: { name: 'THOR Wallet', icon: '⚡' },
  coinbase: { name: 'Coinbase Wallet', icon: '🔵' },
  ledger: { name: 'Ledger', icon: '🔒' },
  manual: { name: 'Watch Only', icon: '👁️' },
};
